import React, { useState } from 'react';
import { X, Phone, Mail, User, Calendar, FileText, Ban, Clock, ThumbsUp, Star, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { 
    value: 'visited', 
    label: 'Nur besucht', 
    icon: Clock, 
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    description: 'Neutral - noch kein Ergebnis'
  },
  { 
    value: 'interested', 
    label: 'Hat Interesse', 
    icon: ThumbsUp, 
    color: 'bg-green-100 text-green-700 border-green-400',
    description: 'Potenzieller Partner'
  },
  { 
    value: 'follow_up', 
    label: 'Follow-up / Termin', 
    icon: Calendar, 
    color: 'bg-yellow-100 text-yellow-700 border-yellow-400',
    description: 'Später nochmal vorbeikommen'
  },
  { 
    value: 'not_interested', 
    label: 'Kein Interesse', 
    icon: Ban, 
    color: 'bg-red-100 text-red-700 border-red-400',
    description: 'Auf Blacklist setzen'
  },
  { 
    value: 'partner', 
    label: 'Als Partner registriert', 
    icon: Star, 
    color: 'bg-brand-green/20 text-brand-green border-brand-green',
    description: 'Erfolg! Partner gewonnen'
  }
];

const BusinessStatusModal = ({ 
  isOpen, 
  onClose, 
  business, 
  token, 
  onStatusUpdated 
}) => {
  const [selectedStatus, setSelectedStatus] = useState('visited');
  const [notes, setNotes] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !business) return null;

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // First register the business contact if it doesn't exist
      await fetch(`${API_URL}/api/business-contacts/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          place_id: business.place_id,
          name: business.name,
          address: business.address,
          business_type: business.business_type,
          lat: business.lat,
          lng: business.lng,
          rating: business.rating,
          user_ratings_total: business.user_ratings_total
        })
      });

      // Then update status
      const response = await fetch(`${API_URL}/api/business-contacts/${business.place_id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: selectedStatus,
          notes,
          contact_person: contactPerson || null,
          contact_phone: contactPhone || null,
          contact_email: contactEmail || null,
          follow_up_date: followUpDate || null,
          follow_up_notes: followUpNotes || null
        })
      });

      if (!response.ok) {
        throw new Error('Status konnte nicht aktualisiert werden');
      }

      const result = await response.json();
      
      if (onStatusUpdated) {
        onStatusUpdated(business.place_id, selectedStatus, result.contact);
      }
      
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedOption = STATUS_OPTIONS.find(s => s.value === selectedStatus);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Besuch abschließen</h2>
            <p className="text-sm text-gray-600 truncate max-w-xs">{business.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Was ist passiert?
            </label>
            <div className="space-y-2">
              {STATUS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedStatus === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setSelectedStatus(option.value)}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      isSelected 
                        ? option.color + ' border-2' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`status-${option.value}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-white/50' : 'bg-gray-100'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{option.label}</div>
                      <div className="text-xs opacity-70">{option.description}</div>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-current"></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Follow-up Date (only for follow_up status) */}
          {selectedStatus === 'follow_up' && (
            <div className="bg-yellow-50 rounded-xl p-4 space-y-3 border border-yellow-200">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Termin-Datum
                </label>
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="follow-up-date"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Termin-Notiz
                </label>
                <input
                  type="text"
                  value={followUpNotes}
                  onChange={(e) => setFollowUpNotes(e.target.value)}
                  placeholder="z.B. Chef ist ab 14 Uhr da"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                />
              </div>
            </div>
          )}

          {/* Warning for Blacklist */}
          {selectedStatus === 'not_interested' && (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-700">Achtung: Blacklist</p>
                <p className="text-sm text-red-600">
                  Dieser Betrieb wird in zukünftigen Touren nicht mehr angezeigt.
                </p>
              </div>
            </div>
          )}

          {/* Contact Info (for interested/follow_up) */}
          {['interested', 'follow_up', 'partner'].includes(selectedStatus) && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Kontaktdaten (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">
                    <User className="w-3 h-3 inline mr-1" />
                    Ansprechpartner
                  </label>
                  <input
                    type="text"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="Herr/Frau..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    <Phone className="w-3 h-3 inline mr-1" />
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+49..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    <Mail className="w-3 h-3 inline mr-1" />
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="email@..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Weitere Anmerkungen..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
              data-testid="notes-input"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 ${
                selectedStatus === 'not_interested' 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : selectedStatus === 'partner'
                    ? 'bg-brand-green hover:bg-brand-green-dark'
                    : ''
              }`}
              data-testid="submit-status-btn"
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessStatusModal;
